import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { PlatformApiClient, PlatformApiError } from "./api";
import {
  MetaWhatsAppReviewApi,
  type MetaWhatsAppReviewEvent,
  type MetaWhatsAppReviewStatus,
} from "./meta-whatsapp-review-api";
import type { OrganizationContext } from "./types";

interface LoadState {
  kind: "loading" | "ready" | "unavailable" | "error";
  status: MetaWhatsAppReviewStatus | null;
  events: readonly MetaWhatsAppReviewEvent[];
  message: string | null;
}

function providerData(value: unknown): readonly Record<string, unknown>[] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return [];
  const data = (value as Record<string, unknown>).data;
  if (!Array.isArray(data)) return [];
  return data.filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null && !Array.isArray(item));
}

function text(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "—";
}

function ReviewReadiness({ status }: { status: MetaWhatsAppReviewStatus }) {
  const checks = [
    ["Webhook", status.readyForWebhookVerification],
    ["Entrantes", status.readyForInbound],
    ["Graph API", status.readyForGraph],
    ["Envíos", status.readyForOutbound],
  ] as const;
  return <div className="meta-review-readiness" aria-label="Estado de integración">
    {checks.map(([label, ready]) => <div key={label}><span>{label}</span><strong className={ready ? "ready" : "pending"}>{ready ? "Listo" : "Pendiente"}</strong></div>)}
  </div>;
}

export function MetaWhatsAppReviewPanel({
  organization,
  api,
  onExpired,
}: {
  organization: OrganizationContext;
  api: PlatformApiClient;
  onExpired: () => void;
}) {
  const review = useMemo(() => new MetaWhatsAppReviewApi(api), [api]);
  const organizationId = organization.organization.id;
  const [state, setState] = useState<LoadState>({ kind: "loading", status: null, events: [], message: null });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phones, setPhones] = useState<readonly Record<string, unknown>[] | null>(null);
  const [templates, setTemplates] = useState<readonly Record<string, unknown>[] | null>(null);

  const handleFailure = useCallback((cause: unknown, fallback: string) => {
    if (cause instanceof PlatformApiError && cause.status === 401) {
      onExpired();
      return;
    }
    setError(cause instanceof Error ? cause.message : fallback);
  }, [onExpired]);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [statusResult, eventResult] = await Promise.all([
        review.status(organizationId),
        review.events(organizationId, 50),
      ]);
      setState({ kind: "ready", status: statusResult.status, events: eventResult.events, message: null });
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) {
        onExpired();
        return;
      }
      if (cause instanceof PlatformApiError && cause.status === 404) {
        setState({ kind: "unavailable", status: null, events: [], message: "WhatsApp todavía no está configurado para este negocio." });
        return;
      }
      setState({ kind: "error", status: null, events: [], message: cause instanceof Error ? cause.message : "No se pudo cargar WhatsApp." });
    }
  }, [onExpired, organizationId, review]);

  useEffect(() => { void reload(); }, [reload]);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy("send"); setError(null); setNotice(null);
    try {
      await review.send(organizationId, String(data.get("to") ?? ""), String(data.get("text") ?? ""));
      form.reset();
      setNotice("Mensaje enviado por WhatsApp y registrado en ClickDialog.");
      await reload();
    } catch (cause) { handleFailure(cause, "No se pudo enviar el mensaje."); }
    finally { setBusy(null); }
  }

  async function loadPhones() {
    setBusy("phones"); setError(null);
    try { setPhones(providerData((await review.phoneNumbers(organizationId)).provider)); }
    catch (cause) { handleFailure(cause, "No se pudieron cargar los números."); }
    finally { setBusy(null); }
  }

  async function loadTemplates() {
    setBusy("templates"); setError(null);
    try { setTemplates(providerData((await review.templates(organizationId, 50)).provider)); }
    catch (cause) { handleFailure(cause, "No se pudieron cargar las plantillas."); }
    finally { setBusy(null); }
  }

  async function subscribe() {
    setBusy("subscribe"); setError(null); setNotice(null);
    try { await review.subscribe(organizationId); setNotice("Aplicación suscripta al WABA autorizado."); }
    catch (cause) { handleFailure(cause, "No se pudo suscribir la aplicación."); }
    finally { setBusy(null); }
  }

  async function createTemplate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy("create-template"); setError(null); setNotice(null);
    try {
      await review.createTemplate(organizationId, {
        name: String(data.get("name") ?? ""),
        language: String(data.get("language") ?? ""),
        category: "UTILITY",
        components: [{ type: "BODY", text: String(data.get("body") ?? "") }],
      });
      form.reset();
      setNotice("Plantilla enviada a Meta. Su estado final depende de la revisión de Meta.");
      await loadTemplates();
    } catch (cause) { handleFailure(cause, "No se pudo crear la plantilla."); }
    finally { setBusy(null); }
  }

  return <section className="meta-review-panel" aria-labelledby="meta-review-title">
    <div className="section-heading meta-review-heading"><div><p className="eyebrow">Integraciones · modo de revisión</p><h2 id="meta-review-title">WhatsApp Business</h2><p>Superficie mínima para demostrar mensajería y administración durante Meta App Review. No muestra credenciales.</p></div><button type="button" onClick={() => void reload()} disabled={state.kind === "loading" || busy !== null}>Actualizar</button></div>

    {state.kind === "loading" && <p className="muted">Comprobando configuración de WhatsApp…</p>}
    {state.kind === "unavailable" && <div className="inline-alert" role="status">{state.message}</div>}
    {state.kind === "error" && <div className="inline-alert" role="alert">{state.message}</div>}
    {error && <div className="inline-alert" role="alert">{error}</div>}
    {notice && <div className="success-alert" role="status">{notice}</div>}

    {state.kind === "ready" && state.status && <>
      <ReviewReadiness status={state.status} />
      <p className="security-note">Los envíos están limitados por un interruptor independiente y una lista de destinos autorizados. Destinos configurados: {state.status.allowedDestinationTails.length ? state.status.allowedDestinationTails.join(", ") : "ninguno"}.</p>

      <div className="meta-review-grid">
        <section className="details-card meta-review-card"><h3>Enviar mensaje de prueba</h3><p>Usá únicamente el número autorizado para la revisión.</p><form onSubmit={send} className="meta-review-form"><label htmlFor="meta-review-to">Destino E.164</label><input id="meta-review-to" name="to" type="tel" placeholder="+549…" required /><label htmlFor="meta-review-text">Mensaje</label><textarea id="meta-review-text" name="text" maxLength={4096} required placeholder="Hola desde ClickDialog" /><button className="primary-button" disabled={busy !== null || !state.status.readyForOutbound}>{busy === "send" ? "Enviando…" : "Enviar por WhatsApp"}</button></form></section>

        <section className="details-card meta-review-card"><h3>Activo autorizado</h3><p>Consulta el WABA configurado sin exponer tokens.</p><div className="button-row"><button type="button" disabled={busy !== null || !state.status.readyForGraph} onClick={() => void loadPhones()}>{busy === "phones" ? "Cargando…" : "Cargar números"}</button><button type="button" disabled={busy !== null || !state.status.readyForGraph} onClick={() => void subscribe()}>{busy === "subscribe" ? "Suscribiendo…" : "Suscribir webhook"}</button></div>{phones !== null && (phones.length === 0 ? <p className="muted">Meta no devolvió números.</p> : <dl className="meta-review-list">{phones.map((phone, index) => <div key={String(phone.id ?? index)}><dt>{text(phone.verified_name)}</dt><dd>{text(phone.display_phone_number)} · {text(phone.status)} · calidad {text(phone.quality_rating)}</dd></div>)}</dl>)}</section>

        <section className="details-card meta-review-card meta-review-wide"><div className="section-heading"><div><h3>Plantillas de mensaje</h3><p>Lista y crea una plantilla de utilidad para demostrar `whatsapp_business_management`.</p></div><button type="button" disabled={busy !== null || !state.status.readyForGraph} onClick={() => void loadTemplates()}>{busy === "templates" ? "Cargando…" : "Cargar plantillas"}</button></div>{templates !== null && (templates.length === 0 ? <p className="muted">No hay plantillas para mostrar.</p> : <div className="meta-template-table">{templates.map((template, index) => <div key={String(template.id ?? `${template.name ?? "template"}-${index}`)}><strong>{text(template.name)}</strong><span>{text(template.language)}</span><span>{text(template.category)}</span><span>{text(template.status)}</span></div>)}</div>)}<form onSubmit={createTemplate} className="meta-review-form meta-template-form"><label htmlFor="meta-template-name">Nombre</label><input id="meta-template-name" name="name" pattern="[a-z0-9_]+" placeholder="confirmacion_turno" required /><label htmlFor="meta-template-language">Idioma</label><input id="meta-template-language" name="language" defaultValue="es_AR" required /><label htmlFor="meta-template-body">Texto</label><textarea id="meta-template-body" name="body" placeholder="Tu turno está confirmado." required /><button type="submit" disabled={busy !== null || !state.status.readyForGraph}>{busy === "create-template" ? "Creando…" : "Crear plantilla de utilidad"}</button></form></section>
      </div>

      <section className="details-card meta-review-history"><div className="section-heading"><div><h3>Conversaciones de revisión</h3><p>Mensajes entrantes y salientes registrados por el adapter de Meta.</p></div><span className="status-pill success">{state.events.length} eventos</span></div>{state.events.length === 0 ? <p className="muted">Todavía no hay mensajes registrados.</p> : <ol>{state.events.map((event) => <li key={event.id}><div><strong>{event.direction === "inbound" ? event.profileName ?? "Cliente" : "ClickDialog"}</strong><span>{event.direction === "inbound" ? "Entrante" : "Saliente"} · {event.counterpartyTail}</span></div><p>{event.messageText}</p><time dateTime={event.createdAt}>{new Date(event.createdAt).toLocaleString()}</time></li>)}</ol>}</section>
    </>}
  </section>;
}
